import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import MeanChart from './MeanChart';
import measurementService from '../../../../services/measurementService';
import patientService from '../../../../services/patientService';
import { toast } from 'react-toastify';
import EmailModal from '../../../../components/EmailModal/EmailModal';
import emailService from '../../../../services/emailService';
import { exportElementToPdf, printElement } from '../../../../utils/reportExportUtils';
import { getPresetDateRangeParams } from '../../../../utils/measurementUtils';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import MeasurementChartHeader from '../../ChartHeader/MeasurementChartHeader';
import './DailyTrendChart.css';

const DailyTrendChart = ({ practiceId: propPracticeId, patientId: propPatientId }) => {
  const params = useParams();
  const practiceId = propPracticeId || params.practiceId;
  const patientId = propPatientId || params.patientId;

  const [queryType, setQueryType] = useState('dateRange');
  const [dateRange, setDateRange] = useState('Last 30 days');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const cacheKey = `chart_patient_meta_${practiceId}_${patientId}`;
  const [patientData, setPatientData] = useState(() => {
    try {
      const raw = sessionStorage.getItem(cacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queryLoading, setQueryLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const { updateBreadcrumbs } = useBreadcrumb();

  // Fetch patient data on mount
  useEffect(() => {
    if (practiceId && patientId) {
      setQueryType('dateRange');
      setDateRange('Last 30 days');
      setFromDate('');
      setToDate('');
      fetchPatientData();
    }
  }, [practiceId, patientId]);

  // Fetch measurements when query parameters change
  useEffect(() => {
    if (practiceId && patientId) {
      fetchMeasurements();
    }
  }, [practiceId, patientId, dateRange, fromDate, toDate]);

  // Breadcrumbs effect
  useEffect(() => {
    if (patientData?.patient && practiceId && patientId) {
      updateBreadcrumbs([
        { label: 'Practices', path: '/practices' },
        { label: patientData.patient.practice_name || 'Practice', path: `/practices/${practiceId}` },
        { label: 'Patients', path: `/practices/${practiceId}/patients` },
        {
          label: `${patientData.patient.last_name || ''}, ${patientData.patient.first_name || ''}`.replace(/^, | ,$|(, )+/g, '$1').trim() || 'Patient',
          path: `/practices/${practiceId}/patients/${patientId}`
        },
        { label: 'Blood Pressure Chart', path: null, active: true }
      ]);
    }
  }, [patientData, practiceId, patientId, updateBreadcrumbs]);

  const fetchPatientData = async () => {
    try {
      const response = await patientService.getMeasurementHeaderFast(practiceId, patientId);
      if (response.data.success) {
        setPatientData(response.data.data);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(response.data.data));
        } catch {
          // ignore cache write errors
        }
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
      toast.error('Failed to load patient data');
    }
  };

  const fetchMeasurements = async () => {
    try {
      setQueryLoading(true);
      let fromDateParam, toDateParam;

      if (queryType === 'dateRange') {
        const params = getPresetDateRangeParams(dateRange);
        fromDateParam = params.fromDateParam;
        toDateParam = params.toDateParam;
      } else {
        // Inputs are already YYYY-MM-DD from type="date"
        if (fromDate && toDate) {
          if (fromDate.includes('/')) {
            const [fromMonth, fromDay, fromYear] = fromDate.split('/');
            const [toMonth, toDay, toYear] = toDate.split('/');
            fromDateParam = `${fromYear}-${fromMonth.padStart(2, '0')}-${fromDay.padStart(2, '0')}`;
            toDateParam = `${toYear}-${toMonth.padStart(2, '0')}-${toDay.padStart(2, '0')}`;
          } else {
            fromDateParam = fromDate;
            toDateParam = toDate;
          }
        }
      }

      if (fromDateParam && toDateParam) {
        const response = await measurementService.getBloodPressureByDateRange(
          practiceId,
          patientId,
          fromDateParam,
          toDateParam,
          'date'
        );
        if (response.data?.success) {
          setMeasurements(response.data.data || []);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching measurements:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load measurements');
      }
      setMeasurements([]);
      setLoading(false);
    } finally {
      setQueryLoading(false);
    }
  };

  const handleQuery = () => {
    fetchMeasurements();
  };

  const patientName = patientData?.patient
    ? `${patientData.patient.first_name || ''} ${patientData.patient.last_name || ''}`.trim()
    : 'Loading...';
  const patientDOB = patientData?.patient?.date_of_birth
    ? new Date(patientData.patient.date_of_birth).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    : 'Loading...';
  const patientVitals = patientData?.patient?.vitals || '';

  // Calculate daily averages for chart - include all measurements (including ForaCare)
  const chartData = useMemo(() => {
    if (!measurements || measurements.length === 0) return { dates: [], sysData: [], diaData: [], measurementDetails: [] };

    // Group measurements by date and store details for tooltips
    const dailyData = {};
    const measurementDetails = []; // Store individual measurements for tooltips

    measurements.forEach(m => {
      const date = m.measurement_date || m.measure_new_date_time?.split('T')[0] || m.created_at?.split('T')[0];
      const time = m.measurement_time ||
        m.measure_new_date_time?.split('T')[1]?.slice(0, 5) ||
        m.measure_date_time?.split(' ')[1]?.slice(0, 5) ||
        m.created_at?.split('T')[1]?.slice(0, 5) ||
        '';

      if (!dailyData[date]) {
        dailyData[date] = { sys: [], dia: [], measurements: [] };
      }

      if (m.systolic_pressure != null) dailyData[date].sys.push(parseFloat(m.systolic_pressure));
      if (m.diastolic_pressure != null) dailyData[date].dia.push(parseFloat(m.diastolic_pressure));

      // Store measurement details for tooltip
      if (m.systolic_pressure != null || m.diastolic_pressure != null) {
        dailyData[date].measurements.push({
          date,
          time,
          sys: m.systolic_pressure ? parseFloat(m.systolic_pressure) : null,
          dia: m.diastolic_pressure ? parseFloat(m.diastolic_pressure) : null,
          fullDateTime: m.measure_new_date_time || m.measure_date_time || m.created_at
        });
      }
    });

    // Calculate averages and sort by date
    const sortedDates = Object.keys(dailyData).sort((a, b) => new Date(a) - new Date(b));
    const sysData = sortedDates.map(date => {
      const values = dailyData[date].sys;
      return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null;
    });
    const diaData = sortedDates.map(date => {
      const values = dailyData[date].dia;
      return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null;
    });

    // Store measurement details indexed by date
    const detailsByDate = {};
    sortedDates.forEach(date => {
      detailsByDate[date] = dailyData[date].measurements;
    });

    return { dates: sortedDates, sysData, diaData, measurementDetails: detailsByDate };
  }, [measurements]);

  // Chart dimensions and scaling
  const chartWidth = 1100;
  const chartHeight = 400;
  const padding = { top: 20, right: 30, bottom: 65, left: 70 };
  const plotWidth = chartWidth - padding.left - padding.right;
  const plotHeight = chartHeight - padding.top - padding.bottom;
  const maxY = 200;
  const minY = 0;

  // Scale functions
  const scaleX = (index) => {
    if (chartData.dates.length <= 1) return padding.left;
    return padding.left + (index / (chartData.dates.length - 1)) * plotWidth;
  };

  const scaleY = (value) => {
    if (value == null) return null;
    return padding.top + plotHeight - ((value - minY) / (maxY - minY)) * plotHeight;
  };

  // Generate SVG path for line
  const generatePath = (data) => {
    const points = data
      .map((value, index) => {
        const y = scaleY(value);
        if (y == null) return null;
        return { x: scaleX(index), y };
      })
      .filter(p => p != null);

    if (points.length === 0) return '';

    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      path += ` L ${points[i].x} ${points[i].y}`;
    }
    return path;
  };

  // Format date for x-axis
  const formatChartDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
  };

  // Format date/time for tooltip
  const formatTooltipDateTime = (dateStr, timeStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    if (timeStr) {
      // Format time (HH:MM to HH:MM AM/PM)
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${month}-${day}-${year} ${displayHour}:${minutes} ${ampm}`;
    }
    return `${month}-${day}-${year}`;
  };

  // Tooltip state
  const [tooltip, setTooltip] = useState(null);

  const handlePrint = () => {
    try {
      const el = document.getElementById('dtc-pdf-content');
      printElement(el, 'Blood Pressure Chart');
    } catch (e) {
      toast.error('Print failed');
    }
  };

  const handleExport = async () => {
    const el = document.getElementById('dtc-pdf-content');
    if (!el) return;
    try {
      await exportElementToPdf(el, 'BP-Chart.pdf');
      toast.success('Exported to PDF');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  return (
    <div className="daily-trend-chart-page">
      <MeasurementChartHeader
        practiceId={practiceId}
        patientId={patientId}
        vitals={patientVitals}
        activeVital="blood-pressure"
        patientName={patientName}
        patientDOB={patientDOB}
        queryType={queryType}
        setQueryType={setQueryType}
        dateRange={dateRange}
        setDateRange={setDateRange}
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        onQuery={handleQuery}
        onPrint={handlePrint}
        onExport={handleExport}
        onEmail={() => setShowEmailModal(true)}
        hasData={measurements.length > 0}
      />

      {/* Daily Trend Chart Section - scrollable */}
      <div className="dtc-charts-scroll" id="dtc-pdf-content">
        <fieldset className="dtc-fieldset-custom" style={{ padding: '20px', background: 'white' }}>
          <legend className="dtc-legend-custom">Daily Trend Chart</legend>
          <div className="dtc-chart-container">
            <div className="dtc-chart-title">Daily Trend Chart of Blood Pressure</div>
            <div className="dtc-chart-legend">
              <div className="dtc-legend-item">
                <div className="dtc-legend-line dtc-legend-systolic"></div>
                <span>Systolic Pressure</span>
              </div>
              <div className="dtc-legend-item">
                <div className="dtc-legend-line dtc-legend-diastolic"></div>
                <span>Diastolic Pressure</span>
              </div>
            </div>
            <div className="dtc-chart-area" style={{ overflowX: 'auto', padding: '20px 0', border: 'none' }}>
              {loading || queryLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>Loading chart data...</div>
              ) : chartData.dates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>No data available for the selected period</div>
              ) : (
                <div className="dtc-chart-placeholder" style={{ display: 'block', minWidth: `${chartWidth}px`, margin: '0 auto', position: 'relative' }}>
                  <svg width={chartWidth} height={chartHeight} style={{ display: 'block' }}>
                    {/* Y-axis Label */}
                    <text
                      x={15}
                      y={padding.top + plotHeight / 2}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="#333"
                      fontSize="14"
                      fontWeight="600"
                      transform={`rotate(-90, 15, ${padding.top + plotHeight / 2})`}
                    >
                      Blood Pressure mmHg
                    </text>

                    {/* Grid background and outer border */}
                    <rect x={padding.left} y={padding.top} width={plotWidth} height={plotHeight} fill="none" stroke="#e0e0e0" strokeWidth="1" />

                    {/* Horizontal Grid lines and Y-axis text */}
                    {[0, 20, 40, 60, 80, 100, 120, 140, 160, 180, 200].map(value => {
                      const y = scaleY(value);
                      if (y == null) return null;
                      return (
                        <g key={`y-${value}`}>
                          {/* Grid line */}
                          <line
                            x1={padding.left}
                            y1={y}
                            x2={chartWidth - padding.right}
                            y2={y}
                            stroke="#ECEDEE"
                            strokeWidth="1"
                          />
                          {/* Y-axis Text */}
                          <text
                            x={padding.left - 10}
                            y={y}
                            textAnchor="end"
                            dominantBaseline="middle"
                            fill="#666"
                            fontSize="12"
                          >
                            {value}
                          </text>
                        </g>
                      );
                    })}

                    {/* Target Zone */}
                    {scaleY(150) != null && scaleY(100) != null && (
                      <g>
                        <rect
                          x={padding.left}
                          y={scaleY(150)}
                          width={plotWidth}
                          height={scaleY(100) - scaleY(150)}
                          fill="#eaf5e9"
                        />
                        <text
                          x={padding.left + plotWidth / 2}
                          y={scaleY(125)}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="#2e7d32"
                          fontSize="13"
                          fontWeight="bold"
                        >
                          Target Zone
                        </text>
                      </g>
                    )}

                    {/* Vertical grid lines for dates and X-axis text */}
                    {chartData.dates.map((date, index) => {
                      const x = scaleX(index);
                      return (
                        <g key={`x-${date}`}>
                          <line
                            x1={x}
                            y1={padding.top}
                            x2={x}
                            y2={chartHeight - padding.bottom}
                            stroke="#e0e0e0"
                            strokeWidth="1"
                            strokeDasharray="none"
                          />
                          {/* X-axis Text */}
                          {(index % Math.max(1, Math.floor(chartData.dates.length / 10)) === 0 || index === chartData.dates.length - 1) && (
                            <text
                              x={x}
                              y={chartHeight - padding.bottom + 15}
                              textAnchor="end"
                              dominantBaseline="middle"
                              fill="#666"
                              fontSize="11"
                              transform={`rotate(-45, ${x}, ${chartHeight - padding.bottom + 15})`}
                            >
                              {formatChartDate(date)}
                            </text>
                          )}
                        </g>
                      );
                    })}

                    {/* Systolic line (red) */}
                    {chartData.sysData.some(v => v != null) && (
                      <path
                        d={generatePath(chartData.sysData)}
                        fill="none"
                        stroke="#e53e3e"
                        strokeWidth="3"
                      />
                    )}

                    {/* Diastolic line (blue) */}
                    {chartData.diaData.some(v => v != null) && (
                      <path
                        d={generatePath(chartData.diaData)}
                        fill="none"
                        stroke="#3182ce"
                        strokeWidth="3"
                      />
                    )}

                    {/* Data points with hover tooltips */}
                    {chartData.dates.map((date, index) => {
                      const sysValue = chartData.sysData[index];
                      const diaValue = chartData.diaData[index];
                      const x = scaleX(index);
                      const measurements = chartData.measurementDetails[date] || [];
                      const firstMeasurement = measurements[0] || {};

                      if (sysValue == null && diaValue == null) return null;

                      const handleMouseEnter = (e) => {
                        const rect = e.currentTarget.closest('.dtc-chart-placeholder')?.getBoundingClientRect();
                        if (!rect) return;

                        setTooltip({
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                          date,
                          sysValue,
                          diaValue,
                          time: firstMeasurement.time || '',
                          measurements
                        });
                      };

                      const handleMouseLeave = () => {
                        setTooltip(null);
                      };

                      return (
                        <g key={`points-${date}`}>
                          <line
                            x1={x}
                            y1={padding.top}
                            x2={x}
                            y2={chartHeight - padding.bottom}
                            stroke="transparent"
                            strokeWidth="20"
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                          />
                          {sysValue != null && (
                            <circle
                              cx={x}
                              cy={scaleY(sysValue)}
                              r="6"
                              fill="#ffffff"
                              stroke="#e53e3e"
                              strokeWidth="3"
                              style={{ cursor: 'pointer' }}
                              onMouseEnter={handleMouseEnter}
                              onMouseLeave={handleMouseLeave}
                            />
                          )}
                          {diaValue != null && (
                            <circle
                              cx={x}
                              cy={scaleY(diaValue)}
                              r="6"
                              fill="#ffffff"
                              stroke="#3182ce"
                              strokeWidth="3"
                              style={{ cursor: 'pointer' }}
                              onMouseEnter={handleMouseEnter}
                              onMouseLeave={handleMouseLeave}
                            />
                          )}
                        </g>
                      );
                    })}
                  </svg>

                  <div style={{ textAlign: 'center', marginTop: '10px', color: '#666', fontSize: '13px', fontWeight: '500' }}>
                    Time of Day (6 AM - 10 PM)
                  </div>

                  {/* Tooltip */}
                  {tooltip && (
                    <div
                      className="dtc-tooltip"
                      style={{
                        left: `${tooltip.x}px`,
                        top: `${tooltip.y - 100}px`,
                        transform: 'translateX(-50%)',
                        position: 'absolute'
                      }}
                    >
                      <div className="dtc-tooltip-content" style={{ background: 'white', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <div className="dtc-tooltip-date" style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                          {formatTooltipDateTime(tooltip.date, tooltip.time)}
                        </div>
                        {tooltip.sysValue != null && (
                          <div className="dtc-tooltip-item" style={{ fontSize: '12px' }}>
                            <span className="dtc-tooltip-label">Systolic Pressure: </span>
                            <span className="dtc-tooltip-value" style={{ color: '#e53e3e', fontWeight: 'bold' }}>{tooltip.sysValue.toFixed(2)} mmHg</span>
                          </div>
                        )}
                        {tooltip.diaValue != null && (
                          <div className="dtc-tooltip-item" style={{ fontSize: '12px', marginTop: '4px' }}>
                            <span className="dtc-tooltip-label">Diastolic Pressure: </span>
                            <span className="dtc-tooltip-value" style={{ color: '#3182ce', fontWeight: 'bold' }}>{tooltip.diaValue.toFixed(2)} mmHg</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </fieldset>

        {/* Mean Chart Section */}
        <div className="dtc-mean-chart-wrapper">
          <MeanChart practiceId={practiceId} patientId={patientId} />
        </div>
      </div>{/* end dtc-charts-scroll */}

      {/* Email Modal */}
      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={async (emailData) => {
          try {
            if (emailData.attachments?.length) {
              toast.info('Attachments are not supported yet. Sending without attachments.');
            }
            const payload = {
              to: emailData.to,
              cc: emailData.cc,
              bcc: emailData.bcc,
              subject: emailData.subject,
              text: emailData.content,
              html: `<pre style="font-family: Arial, sans-serif; white-space: pre-wrap;">${String(emailData.content || '').replace(/</g, '&lt;')}</pre>`,
              event_id: 9
            };
            const res = await emailService.sendCustomNotification(payload);
            if (!res.success) throw new Error(res.error || res.message);
            toast.success('Email queued successfully');
            setShowEmailModal(false);
          } catch (e) {
            toast.error(e?.message || 'Failed to send email');
          }
        }}
        defaultSubject={`Blood Pressure Chart Report - ${new Date().toLocaleDateString()}`}
        defaultContent={`Blood Pressure Chart Report\nPatient: ${patientName} (DOB: ${patientDOB})\nGenerated: ${new Date().toLocaleString()}\nTotal Records: ${measurements.length}`}
      />
    </div>
  );
};

export default DailyTrendChart;

